import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, X } from 'lucide-react';
import { Button } from './ui/button';
import { api, errMsg, fileUrl } from '../lib/api';
import { compressImage } from '../lib/image';

// Immediate-mode photo uploader for an existing job (condition or work photos)
export const PhotoUploader = ({ jobId, kind, photos, onChanged, canDelete = false, max }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (max && photos.length + files.length > max) {
      toast.error(`Maximum ${max} ${kind} photos per job.`);
      return;
    }
    setBusy(true);
    try {
      for (const f of files) {
        const compressed = await compressImage(f);
        const fd = new FormData();
        fd.append('file', compressed);
        fd.append('kind', kind);
        await api.post(`/jobs/${jobId}/photos`, fd);
      }
      toast.success(files.length > 1 ? `${files.length} photos added` : 'Photo added');
      onChanged && onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (photoId) => {
    try {
      await api.delete(`/jobs/${jobId}/photos/${photoId}`);
      toast.success('Photo removed');
      onChanged && onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {photos.map((p) => (
          <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
            <img src={fileUrl(p.url)} alt={p.caption || kind} className="h-full w-full object-cover" loading="lazy" />
            {canDelete && (
              <button
                data-testid="photo-delete-button"
                aria-label="Remove photo"
                onClick={() => remove(p.id)}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        <button
          data-testid={`photo-upload-${kind}-button`}
          type="button"
          onClick={pick}
          disabled={busy}
          className="aspect-square rounded-xl border border-dashed border-[hsl(var(--border))] flex flex-col items-center justify-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:bg-black/5 transition-colors disabled:opacity-50"
        >
          <Camera className="h-5 w-5" />
          {busy ? 'Uploading…' : 'Add photo'}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple hidden onChange={onFiles} />
    </div>
  );
};
